import Attrs from '../models/Attrs'
import type { BaseBuilderVersion, Flow, FlowPage, MediaEmbed } from '../models/types'
import { generateElementSelectors } from '../styles/selectors'
import replaceStorageURLsFromFirebaseToCloudflare from '../helpers/replaceStorageURLsFromFirebaseToCloudflare'
import renderTemplate from '../helpers/templating'
import { getBaseBuilderVersion } from '../helpers/utils'

const CONTROLLER = 'components--media-embed'

export default function renderMediaEmbed(
  component: MediaEmbed,
  attrs: Attrs,
  page: FlowPage,
  flow: Flow,
  userData?: { [key: string]: unknown }
): string {
  attrs
    .addController(CONTROLLER)
    .addDOMClass(['MediaEmbed'])
    .addClass(CONTROLLER, 'clickable', 'clickable')
  if (component.roundedCorners) attrs.addDOMClass('roundedCorners')

  const srcWithURLReplacement = replaceStorageURLsFromFirebaseToCloudflare(component.src) || ''
  const srcAfterTemplate =
    replaceStorageURLsFromFirebaseToCloudflare(renderTemplate(srcWithURLReplacement, userData)) ||
    ''
  const src = srcAfterTemplate

  const placeholderImageUrlWithURLReplacement =
    replaceStorageURLsFromFirebaseToCloudflare(component.placeholder_image_url) || ''
  const placeholderImageUrlAfterTemplate =
    replaceStorageURLsFromFirebaseToCloudflare(
      renderTemplate(placeholderImageUrlWithURLReplacement, userData)
    ) || ''
  const placeholder_image_url = placeholderImageUrlAfterTemplate

  const isVideoTag = srcWithURLReplacement ? tagIsVideo(srcAfterTemplate) : false

  const embedCodeWithURLReplacement =
    replaceStorageURLsFromFirebaseToCloudflare(component.embed_code_raw || component.embed_code) ||
    ''
  const embedCodeAfterTemplate =
    replaceStorageURLsFromFirebaseToCloudflare(
      renderTemplate(embedCodeWithURLReplacement, userData)
    ) || ''
  const embedCode = embedCodeAfterTemplate

  const centralPlayButton = component.centralPlayButton || false
  const shouldHideControls = component.silentAutoplayPreview && component.hide_controls
  const shouldShowPlayButton = centralPlayButton && !shouldHideControls

  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  let inner: string = ''
  if (isVideoTag) {
    const videoClasses = generateElementSelectors({
      elementType: 'Video',
      component,
      page,
      builderVersion,
    })

    attrs
      .addClass(CONTROLLER, 'video', videoClasses.join(' '))
      .addValue(CONTROLLER, 'autoplay', `${Boolean(component.centralPlayButton)}`)
      .addValueIfExists(CONTROLLER, 'src', srcAfterTemplate)
    if (component.page_reactive_classes) attrs.addReactiveElement()

    const videoTagAttrs = new Attrs(attrs.static_render)
      .addTarget(CONTROLLER, 'videoPlaceholder')
      .add('controls', 'controls')

    if (placeholder_image_url && shouldShowPlayButton) {
      const imgClasses = generateElementSelectors({
        elementType: 'PlaceholderImage',
        component,
        page,
        builderVersion,
      })
      if (component.page_reactive_classes) videoTagAttrs.addReactiveElement()
      videoTagAttrs.addDOMClass(imgClasses)

      const imageTagAttrs = videoTagAttrs
        .remove('controls')
        .add('src', placeholderImageUrlAfterTemplate /*imagePlaceholderAfterTemplate*/)

      //* Add template controller if the placeholder_image_url has {{
      if (component.placeholder_image_url && component.placeholder_image_url.includes('{{'))
        imageTagAttrs
          .addController('template')
          .addValue('template', 'target', 'src')
          .addValueIfExists(
            'template',
            'text',
            encodeURIComponent(placeholderImageUrlWithURLReplacement || '')
          )
          .addValueIfExists('template', 'always-rerender', component.always_rerender)

      inner = `<img ${imageTagAttrs} alt="Placeholder"></img>`
    } else {
      const autoplay = component.silentAutoplayPreview ? 'autoplay' : ''
      videoTagAttrs.addDOMClass(videoClasses).add('src', srcAfterTemplate!)

      //* Add template controller if the src has {{
      if (component.src && component.src.includes('{{'))
        videoTagAttrs
          .addController('template')
          .addValue('template', 'target', 'src')
          .addValueIfExists('template', 'text', encodeURIComponent(srcWithURLReplacement || ''))
          .addValueIfExists('template', 'always-rerender', component.always_rerender)

      inner = `<video ${videoTagAttrs} muted loop ${autoplay}></video>`
    }
  } else if (embedCodeWithURLReplacement) {
    const embedAttrs = new Attrs(attrs.static_render)
      .addDOMClass(
        ...generateElementSelectors({
          elementType: 'Embed',
          component,
          page,
          builderVersion,
        })
      )
      .add('style', 'width:100%;height:auto;')
    if (component.page_reactive_classes) embedAttrs.addReactiveElement()
    if (!component.embed_code_raw) {
      //* Add template controller if the embed_code has {{
      if (component.embed_code && component.embed_code.includes('{{'))
        embedAttrs
          .addController('template')
          .addValueIfExists(
            'template',
            'text',
            encodeURIComponent(embedCodeWithURLReplacement || '')
          )
          .addValueIfExists('template', 'always-rerender', component.always_rerender)
    }

    inner = `<div ${embedAttrs}>${embedCodeAfterTemplate}</div>`
  } else {
    const iframeContainerClasses = generateElementSelectors({
      elementType: 'IFrameContainer',
      component,
      page,
      builderVersion,
    })
    const iframeClasses = generateElementSelectors({
      elementType: 'IFrame',
      component,
      page,
      builderVersion,
    })
    const iframeAttrs = new Attrs(attrs.static_render)
      .add('src', srcAfterTemplate!)
      .addDOMClass(iframeClasses)
      .add('frameborder', '0')
      .add('style', 'position:absolute;top:0;left:0;width:100%;height:100%;')

    //* Add template controller if the src has {{
    if (component.src && component.src.includes('{{'))
      iframeAttrs
        .addController('template')
        .addValue('template', 'target', 'src')
        .addValueIfExists('template', 'text', encodeURIComponent(srcWithURLReplacement || ''))

    const innerAttrs = new Attrs(attrs.static_render)
      .addDOMClass(iframeContainerClasses)
      .add('style', 'position:relative;padding-bottom:56.25%;height:0;')
    if (component.page_reactive_classes) {
      iframeAttrs.addReactiveElement()
      innerAttrs.addReactiveElement()
    }
    const iframe = `<iframe ${iframeAttrs} webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe>`
    inner = `<div ${innerAttrs}>${iframe}</div>`
  }

  const playButton = shouldShowPlayButton
    ? createPlayButton(component, page, attrs.static_render, builderVersion)
    : ''

  return `<div ${attrs}>${inner}${playButton}</div>`
}

function createPlayButton(
  component: MediaEmbed,
  page: FlowPage,
  attrsStaticRender: boolean,
  builderVersion: BaseBuilderVersion
): string {
  const attrs = new Attrs(attrsStaticRender)
    .addIcon('fa-solid', 'play')
    .addAction(`click->${CONTROLLER}#activatePlayer`)
    .addTarget(CONTROLLER, 'playButton')
    .add('type', 'button')
    .addDOMClass([
      'video-play-button',
      ...generateElementSelectors({
        elementType: 'PlayButton',
        component,
        page,
        builderVersion,
      }),
    ])
  if (component.page_reactive_classes) attrs.addReactiveElement()
  return `<button ${attrs}></button>`
}

function tagIsVideo(srcAfterTemplate: string): boolean {
  return srcAfterTemplate.endsWith('.mp4') || srcAfterTemplate.endsWith('.mov')
}
